"use client";

import { ReactNode, useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useSubscription } from '@/hooks/useSubscription';
import { Button } from '@/components/ui/button';
import { clientLogger } from '@/lib/client-logger';

interface TenantColors {
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
}

interface SubscriptionGuardProps {
  children: ReactNode;
  requiredPlan?: 'FREE' | 'STARTER' | 'PREMIUM';
  action?: 'create_campaign' | 'add_clue' | 'claim_asset';
  currentCount?: number;
  fallback?: ReactNode;
  showUpgradePrompt?: boolean;
}

export function SubscriptionGuard({
  children,
  requiredPlan,
  action: _action,
  currentCount: _currentCount,
  fallback,
  showUpgradePrompt = true,
}: SubscriptionGuardProps) {
  const { subscription, isLoading } = useSubscription();
  const _router = useRouter();

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-sm text-gray-600">Checking subscription...</span>
      </div>
    );
  }

  // Check if subscription is active
  if (!subscription?.isActive) {
    return fallback || (
      <SubscriptionUpgradePrompt
        title="Subscription Required"
        message="Your subscription is not active. Please upgrade to access this feature."
        upgradeRequired="STARTER"
        showUpgradePrompt={showUpgradePrompt}
      />
    );
  }

  // Check required plan
  if (requiredPlan) {
    const planHierarchy = { FREE: 0, STARTER: 1, PREMIUM: 2 };
    const currentPlanLevel = planHierarchy[subscription.plan];
    const requiredPlanLevel = planHierarchy[requiredPlan];

    if (currentPlanLevel < requiredPlanLevel) {
      return fallback || (
        <SubscriptionUpgradePrompt
          title={`${requiredPlan} Plan Required`}
          message={`This feature requires a ${requiredPlan.toLowerCase()} plan or higher.`}
          upgradeRequired={requiredPlan}
          showUpgradePrompt={showUpgradePrompt}
        />
      );
    }
  }

  // Check action limits (this would need to be async, so we'll handle it differently)
  // For now, we'll just render the children and let individual components handle limit checks

  return <>{children}</>;
}

interface SubscriptionUpgradePromptProps {
  title: string;
  message: string;
  upgradeRequired: string;
  showUpgradePrompt: boolean;
}

function SubscriptionUpgradePrompt({
  title,
  message,
  upgradeRequired,
  showUpgradePrompt,
}: SubscriptionUpgradePromptProps) {
  const router = useRouter();
  const [tenantColors, setTenantColors] = useState<TenantColors | null>(null);

  useEffect(() => {
    const fetchTenantColors = async () => {
      try {
        const response = await fetch("/api/tenant/colors");
        if (response.ok) {
          const data = await response.json();
          if (data.colors) {
            setTenantColors(data.colors);
          }
        }
      } catch (error) {
        clientLogger.error("Error fetching tenant colors", {
          error: error instanceof Error ? error.message : "Unknown error"
        });
      }
    };
    fetchTenantColors();
  }, []);

  const isWhitelabel = tenantColors !== null;

  if (!showUpgradePrompt) {
    return (
      <div
        className={`text-center p-6 rounded-lg border ${!isWhitelabel ? 'bg-gray-50' : ''}`}
        style={isWhitelabel && tenantColors ? {
          backgroundColor: tenantColors.primaryColor,
          borderColor: tenantColors.primaryColor,
        } : undefined}
      >
        <div className={isWhitelabel ? "text-white/60 mb-2" : "text-gray-400 mb-2"}>
          <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <p className={`text-sm ${isWhitelabel ? "text-white/80" : "text-gray-600"}`}>Feature not available</p>
      </div>
    );
  }

  return (
    <div
      className={`text-center p-6 rounded-lg border ${!isWhitelabel ? 'bg-yellow-50 border-yellow-200' : ''}`}
      style={isWhitelabel && tenantColors ? {
        backgroundColor: tenantColors.primaryColor,
        borderColor: tenantColors.primaryColor,
      } : undefined}
    >
      <div className={isWhitelabel ? "text-white/80 mb-4" : "text-yellow-600 mb-4"}>
        <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      </div>

      <h3 className={`text-lg font-semibold mb-2 ${isWhitelabel ? "text-white" : "text-yellow-800"}`}>{title}</h3>
      <p className={`mb-4 ${isWhitelabel ? "text-white/80" : "text-yellow-700"}`}>{message}</p>

      <div className="flex gap-2 justify-center">
        <Button
          onClick={() => router.push(`/dashboard/upgrade?plan=${upgradeRequired}&returnUrl=${window.location.pathname}`)}
          style={isWhitelabel && tenantColors ? {
            backgroundColor: tenantColors.secondaryColor,
            borderColor: tenantColors.secondaryColor,
            color: "white",
          } : undefined}
          className={!isWhitelabel ? "bg-yellow-600 hover:bg-yellow-700 text-white" : ""}
        >
          Upgrade to {upgradeRequired}
        </Button>

        <Button
          onClick={() => router.push('/dashboard/settings')}
          variant="outline"
          style={isWhitelabel && tenantColors ? {
            backgroundColor: tenantColors.accentColor,
            borderColor: tenantColors.accentColor,
            color: "white",
          } : undefined}
          className={!isWhitelabel ? "border-yellow-300 text-yellow-700 hover:bg-yellow-100" : ""}
        >
          View Subscription
        </Button>
      </div>
    </div>
  );
}

// Hook for checking limits in components
export function useSubscriptionLimits() {
  const { subscription, canPerformAction } = useSubscription();

  const checkLimit = async (
    action: 'create_campaign' | 'add_clue' | 'claim_asset',
    currentCount?: number
  ) => {
    if (!subscription?.isActive) {
      return {
        allowed: false,
        reason: 'Subscription not active',
        upgradeRequired: 'STARTER',
      };
    }

    return await canPerformAction(action, currentCount);
  };

  return {
    subscription,
    checkLimit,
    isActive: subscription?.isActive || false,
    plan: subscription?.plan || 'FREE',
    limits: subscription?.limits,
  };
}
